import api from "./axios";
import { AxiosResponse } from "axios";
import type { MarketPriceArea } from "./marketPrice";

interface BackendResponse<T> {
  data: T;
  message: string;
  success: boolean;
  timestamp: string;
}

export interface SeedAvailabilityRow {
  id: string;
  areaId: string;
  hatcheryName: string;
  location: string;
  broodstock: string;
  quantity: string;
  price: number;
  availabilityDate: string;
  contact: string;
  isActive: boolean;
  isPublic?: boolean;
  createdAt: string;
  updatedAt: string;
  area?: MarketPriceArea;
}

export interface CreateSeedAvailabilityDto {
  areaId: string;
  hatcheryName: string;
  location: string;
  broodstock: string;
  quantity: string;
  price: number;
  availabilityDate: string;
  contact: string;
  isActive?: boolean;
  isPublic?: boolean;
}

export type UpdateSeedAvailabilityDto = Partial<CreateSeedAvailabilityDto>;

const unwrap = <T>(response: AxiosResponse<BackendResponse<T> | T>): T => {
  const payload = response.data as BackendResponse<T>;
  if (payload && typeof payload === "object" && "data" in payload) {
    return payload.data;
  }
  return response.data as T;
};

export const fetchSeedAvailabilities = async (params: {
  areaId: string;
  activeOnly?: boolean;
}): Promise<SeedAvailabilityRow[]> => {
  const response = await api.get<BackendResponse<SeedAvailabilityRow[]> | SeedAvailabilityRow[]>("/seed-availability", {
    params,
  });
  return unwrap(response);
};

export const fetchPublicSeedAvailability = async (limit = 2): Promise<SeedAvailabilityRow[]> => {
  const response = await api.get<BackendResponse<SeedAvailabilityRow[]> | SeedAvailabilityRow[]>("/seed-availability/public", {
    params: {limit},
  });
  return unwrap(response);
};

export const createSeedAvailability = async (dto: CreateSeedAvailabilityDto): Promise<SeedAvailabilityRow> => {
  const response = await api.post<BackendResponse<SeedAvailabilityRow> | SeedAvailabilityRow>("/seed-availability", dto);
  return unwrap(response);
};

export const updateSeedAvailability = async (id: string, dto: UpdateSeedAvailabilityDto): Promise<SeedAvailabilityRow> => {
  const response = await api.patch<BackendResponse<SeedAvailabilityRow> | SeedAvailabilityRow>(`/seed-availability/${id}`, dto);
  return unwrap(response);
};

export const deleteSeedAvailability = async (id: string): Promise<void> => {
  await api.delete(`/seed-availability/${id}`);
};
